import { useState, useEffect, useCallback } from 'react'

// Soglie temporali per la gestione bozze (in millisecondi)
const DRAFT_THRESHOLDS = {
  AUTO_LOAD: 15 * 60 * 1000,      // 15 minuti - carica automaticamente
  ASK_USER: 60 * 60 * 1000,       // 1 ora - chiedi all'utente
  MAX_AGE: 60 * 60 * 1000         // 1 ora - cancella automaticamente
}

/**
 * Hook per salvare bozze in localStorage con gestione intelligente del tempo
 * 
 * @param {string} key - Chiave univoca per la bozza
 * @param {any} initialValue - Valore iniziale
 * @returns {Object} { draft, setDraft, clearDraft, hasDraft, draftAge, draftStatus, draftTimestamp }
 */
export function useDraft(key, initialValue = null) {
  const storageKey = `draft_${key}`

  // Leggi bozza raw da localStorage (con timestamp)
  const getRawDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (e) {
      console.warn('Errore lettura bozza:', e)
    }
    return null
  }, [storageKey])

  // Calcola età della bozza in millisecondi
  const getDraftAge = useCallback(() => {
    const raw = getRawDraft()
    if (raw?.timestamp) {
      return Date.now() - raw.timestamp
    }
    return null
  }, [getRawDraft])

  // Determina lo stato della bozza
  const getDraftStatus = useCallback(() => {
    const age = getDraftAge()
    if (age === null) return 'none'
    
    if (age < DRAFT_THRESHOLDS.AUTO_LOAD) return 'auto_load'      // < 15 min
    if (age < DRAFT_THRESHOLDS.ASK_USER) return 'ask_user'        // 15 min - 1 ora
    return 'expired'                                                // > 1 ora
  }, [getDraftAge])

  // Stato iniziale: determina cosa fare con la bozza
  const [draft, setDraftState] = useState(() => {
    const raw = getRawDraft()
    if (!raw?.data) return initialValue
    
    const status = getDraftStatus()
    
    // Bozza scaduta: cancella e ritorna valore iniziale
    if (status === 'expired') {
      try {
        localStorage.removeItem(storageKey)
      } catch (e) {}
      return initialValue
    }
    
    // Bozza valida (auto_load o ask_user): ritorna i dati
    // Il componente deciderà se usarli in base allo status
    return raw.data
  })

  const [draftTimestamp, setDraftTimestamp] = useState(() => {
    const raw = getRawDraft()
    return raw?.timestamp || null
  })

  const [hasDraft, setHasDraft] = useState(() => {
    const status = getDraftStatus()
    return status === 'auto_load' || status === 'ask_user'
  })

  // Età della bozza (aggiornata)
  const draftAge = getDraftAge()
  
  // Status della bozza
  const draftStatus = getDraftStatus()

  // Formatta età in stringa leggibile
  const formatDraftAge = useCallback(() => {
    const age = getDraftAge()
    if (age === null) return null
    
    const minutes = Math.floor(age / 60000)
    if (minutes < 1) return 'pochi secondi fa'
    if (minutes < 60) return `${minutes} minut${minutes === 1 ? 'o' : 'i'} fa`
    
    const hours = Math.floor(minutes / 60)
    return `${hours} or${hours === 1 ? 'a' : 'e'} fa`
  }, [getDraftAge])

  // Salva bozza
  const setDraft = useCallback((value) => {
    setDraftState(value)
    const now = Date.now()
    setDraftTimestamp(now)
    try {
      if (value !== null && value !== undefined) {
        localStorage.setItem(storageKey, JSON.stringify({
          data: value,
          timestamp: now
        }))
        setHasDraft(true)
      }
    } catch (e) {
      console.warn('Errore salvataggio bozza:', e)
    }
  }, [storageKey])

  // Pulisci bozza
  const clearDraft = useCallback(() => {
    setDraftState(initialValue)
    setDraftTimestamp(null)
    try {
      localStorage.removeItem(storageKey)
      setHasDraft(false)
    } catch (e) {
      console.warn('Errore pulizia bozza:', e)
    }
  }, [storageKey, initialValue])

  // Resetta form a valori iniziali (senza cancellare da localStorage)
  const resetToInitial = useCallback(() => {
    setDraftState(initialValue)
  }, [initialValue])

  return { 
    draft, 
    setDraft, 
    clearDraft, 
    hasDraft,
    draftAge,
    draftStatus,        // 'none' | 'auto_load' | 'ask_user' | 'expired'
    draftTimestamp,
    formatDraftAge,
    resetToInitial,
    THRESHOLDS: DRAFT_THRESHOLDS
  }
}

export default useDraft
